import { Wizard, FormField, Select, SpaceBetween, Header, Button, DatePicker, Container, Box, Icon, Modal, Flashbar} from "@cloudscape-design/components";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { processoUpdateService } from "../../services/processoUpdateService";

export default function AtualizarProcessoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [referencias, setReferencias] = useState([]);
  const [selectedRef, setSelectedRef] = useState(null);
  const [processo, setProcesso] = useState(null);
  const [edits, setEdits] = useState({});
  const [flash, setFlash] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  console.log(step);

  //load dropdown once
  useEffect(() => {
    const loadProcessos = async () => {
      setLoading(true);
      try {
        const data = await processoUpdateService.getProcessosForUpdate();
        setReferencias(data.map(p => ({ 
          value: p.referencia, 
          label: `${p.referencia} - ${p.descricao}`,
          description: p.di 
        })));
      } catch (error) {
        console.error('Failed to load processos from API:', error);
        setFlash([{ 
          type: "error", 
          content: error.message || 'Erro ao carregar processos. Verifique sua conexão e tente novamente.',
          dismissible: true 
        }]);
      } finally {
        setLoading(false);
      }
    };

    loadProcessos();
  }, []);

  // load full item when ref chosen
  useEffect(() => {
    if (!selectedRef) return;
    
    const loadProcesso = async () => {
      setLoading(true);
      try {
        const data = await processoUpdateService.getProcessoByReferencia(selectedRef.value);
        setProcesso(data);
        // Clear any previous flash messages on successful load
        setFlash([]);
      } catch (error) {
        console.error('Failed to load processo from API:', error);
        setFlash([{ 
          type: "error", 
          content: error.message || 'Erro ao carregar detalhes do processo.',
          dismissible: true 
        }]);
      } finally {
        setLoading(false);
      }
    };

    loadProcesso();
  }, [selectedRef]);

  const fields = ["di","bu","poso","regime","modal","etd","eta","descricao"];

  const diff = fields.filter(k => edits[k] !== undefined && edits[k] !== processo?.[k]);

  const onSubmit = async () => {
    setLoading(true);
    try {
      const result = await processoUpdateService.updateProcesso(selectedRef.value, edits);
      setFlash([{ 
        type: "success", 
        content: result.message || "Processo atualizado com sucesso!",
        dismissible: true 
      }]);
      setShowConfirm(false);
      // Reset edits after successful update
      setEdits({});
      // Navigate back to processos page after successful update
      setTimeout(() => {
        navigate('/processos');
      }, 1500); // Wait 1.5 seconds to show success message
    } catch (error) {
      console.error('Failed to update processo:', error);
      setFlash([{ 
        type: "error", 
        content: error.message || 'Falha ao atualizar processo.',
        dismissible: true 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
        {flash.length > 0 && (
          <Flashbar 
            items={flash} 
            onDismiss={({ detail }) => {
              setFlash(flash.filter((_, index) => index !== detail.itemIndex));
            }}
          />
        )}
        
        <Wizard
            onNavigate={({ detail }) => setStep(detail.requestedStep)}
            onSubmit={() => setShowConfirm(true)}
            isLoadingNextStep={loading}
            i18nStrings={{
            stepNumberLabel: step => `Etapa ${step}`,
            cancelButton: "Cancelar",
            previousButton: "Anterior",
            nextButton: "Próximo",
            submitButton: "Atualizar Processo"
            }}
            steps={[
            {
            title: "Selecionar Referência",
            content: (
                <FormField label="Referência do Processo">
                <Select 
                  options={referencias} 
                  selectedOption={selectedRef} 
                  filteringType="auto" 
                  filteringPlaceholder="Buscar referência" 
                  onChange={({ detail }) => setSelectedRef(detail.selectedOption)}
                  loading={loading}
                  placeholder="Selecione uma referência"
                />
                </FormField>
            ),
            isOptional: false,
            },
            {
            title: "Editar Detalhes",
            content: processo && (
                <SpaceBetween size="s">
                <Container header={<Header variant="h3">Editando Processo: {selectedRef?.value}</Header>}>
                  <Box color="text-status-info">
                    <strong>Referência:</strong> {selectedRef?.value}<br/>
                    <strong>Descrição:</strong> {processo.descricao}<br/>
                    <strong>DI:</strong> {processo.di}
                  </Box>
                </Container>
                {fields.map(key => {
                    const isDate = key === "etd" || key === "eta";
                    const original = processo[key];
                    const edited = edits[key];
                    const changed = edited !== undefined && edited !== original;

                    const resetValue = () =>
                    setEdits(prev => {
                        const clone = { ...prev };
                        delete clone[key];
                        return clone;
                    });

                    /**
                     * Shared mobile‑friendly row style: items wrap when width is tight
                     * so the content never overflows.  Buttons are icon‑only on narrow
                     * screens to save space.
                     */
                    const rowStyle = {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    width: "100%",
                    };

                    return (
                    <FormField
                        key={key}
                        label={key.toUpperCase()}
                        stretch
                        description={changed ? "modificado" : undefined}
                    >
                        <div style={rowStyle}>
                        {/* value block */}
                        {isDate ? (
                            <DatePicker
                            value={edited ?? original}
                            placeholder="YYYY-MM-DD"
                            onChange={({ detail }) =>
                                setEdits(prev => ({ ...prev, [key]: detail.value }))
                            }
                            />
                        ) : (
                            <span>{original}</span>
                        )}

                        {/* diff preview */}
                        {changed && !isDate && (
                            <span style={{ color: "#888" }}>→ {edited}</span>
                        )}

                        {/* action buttons */}
                        <div style={{ display: "flex", gap: "0.25rem" }}>
                            {changed && (
                            <Button
                                iconName="refresh"
                                variant="inline-icon"
                                ariaLabel="Desfazer"
                                onClick={resetValue}
                            />
                            )}
                            {!isDate && (
                            <Button
                                iconName="edit"
                                variant="inline-icon"
                                ariaLabel="Editar"
                                onClick={() => {
                                const newVal = prompt(`Novo valor para ${key}`, edited ?? original);
                                if (newVal !== null) {
                                    setEdits(prev => ({ ...prev, [key]: newVal }));
                                }
                                }}
                            />
                            )}
                        </div>
                        </div>
                    </FormField>
                    );
                })}
                </SpaceBetween>
            ),
            isOptional: false,
            },
            {
            title: "Revisar e Confirmar",
            content: (
                <>
                {diff.length ? (
                    <SpaceBetween size="xs">
                    {diff.map(k => (
                        <Container key={k} header={<Header variant="h3">{k.toUpperCase()}</Header>}>
                        <Box color="text-status-inactive">{processo[k]}</Box>
                        <Box display="flex" justifyContent="center" padding={{ vertical: 'xs' }}>
                            <Icon name="arrow-down" size="small" />
                        </Box>
                        <Box>{edits[k]}</Box>
                        </Container>
                    ))}
                    </SpaceBetween>
                ) : "Nenhuma alteração."}
                </>
            ),
            isOptional: false,
            },
        ]}
        />

        <Modal
        visible={showConfirm}
        onDismiss={() => setShowConfirm(false)}
        header="Confirmar atualização"
        footer={
            <SpaceBetween direction="horizontal" size="s">
            <Button variant="link" onClick={() => setShowConfirm(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={onSubmit} loading={loading}>
              Atualizar Processo
            </Button>
            </SpaceBetween>
        }
        >
        <SpaceBetween size="s">
          <Box>
            <strong>Tem certeza que deseja atualizar o processo:</strong>
            <Box margin={{ top: 'xs' }} color="text-status-info">
              <strong>Referência:</strong> {selectedRef?.value}
            </Box>
          </Box>
          
          {diff.length > 0 ? (
            <Box>
              <strong>Com as seguintes alterações:</strong>
              <Box margin={{ top: 's' }}>
                <SpaceBetween size="xs">
                  {diff.map(field => (
                    <Box key={field}>
                      <strong>{field.toUpperCase()}:</strong> {processo[field]} → {edits[field]}
                    </Box>
                  ))}
                </SpaceBetween>
              </Box>
            </Box>
          ) : (
            <Box color="text-status-warning">
              Nenhuma alteração foi feita.
            </Box>
          )}
        </SpaceBetween>
        </Modal>
    </>
  );
}